import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { Dayjs } from 'dayjs';

import '../reportStyles.scss';
import { errorNormalizer, dateNormalizer } from '../../../shared/Helpers/functions';
import { CustomButton, Picker, Text } from '../../../shared/uiComponents';
import { ReportFilters, downloadFile } from '../helpers';
import { REPORT_GENERATION_MESSAGE } from '../../../shared/Helpers/constants';
import { DispatchProperties, useSelector } from '../../../redux/store';
import { getDOEReport } from '../../../redux/State/clientSlice/billingSlice';

const isValidOrNull = (d: Dayjs | null): boolean => d === null || d.isValid();

const DOEReport = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const { clientIds, timeError } = useSelector((state) => state.billing.billingFilters);
	const [loading, setLoading] = useState<boolean>(false);
	const [submittedFrom, setSubmittedFrom] = useState<Dayjs | null>(null);
	const [submittedTo, setSubmittedTo] = useState<Dayjs | null>(null);

	const hasInvalidSubmittedDate = useMemo(
		() => !isValidOrNull(submittedFrom) || !isValidOrNull(submittedTo),
		[submittedFrom, submittedTo],
	);

	const downloadHandler = useCallback(() => {
		toast(REPORT_GENERATION_MESSAGE);
		setLoading(true);

		dispatch(
			getDOEReport({
				clientIds,
				submittedFrom: submittedFrom?.isValid() ? dateNormalizer(submittedFrom) : undefined,
				submittedTo: submittedTo?.isValid() ? dateNormalizer(submittedTo) : undefined,
			}),
		)
			.then(unwrapResult)
			.then((response) => downloadFile(response))
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	}, [dispatch, clientIds, submittedFrom, submittedTo]);

	return (
		<>
			<Text title="DOE Funding Report" type="h6" fontWeight="semiBold" className="marginBottom16" />
			<ReportFilters showUsers={false} isMultipleSelect showOnlyNew={false} showDateRange showShorcuts={false}>
				<Picker.CustomDate
					label="Submitted from:"
					value={submittedFrom}
					onChange={setSubmittedFrom}
					disableFuture
				/>
				<Picker.CustomDate
					label="Submitted to:"
					value={submittedTo}
					onChange={setSubmittedTo}
					disableFuture
				/>
			</ReportFilters>
			<div style={{ width: 'max-content' }}>
				<CustomButton
					title="Download"
					onClick={downloadHandler}
					loading={loading}
					disabled={timeError || hasInvalidSubmittedDate}
				/>
			</div>
		</>
	);
};

export default DOEReport;
